import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { AdminCard, AdminModal, BulkDeleteBar, Field, Pill, SelectCheckbox, TableWrap, Td, Th, inputCls, textareaCls, useBulkDelete } from "@/pages/admin/ui";
import ImageUpload from "@/components/admin/ImageUpload";
import LinkInserter from "@/components/admin/LinkInserter";
import ButtonInserter from "@/components/admin/ButtonInserter";
import RichTextEditor from "@/components/admin/RichTextEditor";
import DownloadsManager from "@/components/DownloadsManager";

const EMPTY = {
  name: "",
  slug: "",
  short_description: "",
  description: "",
  features: [],
  custom_specs: [],
  conditions: "",
  category_slug: "windows",
  brand: "",
  price: 0,
  old_price: 0,
  image_url: "",
  gallery: [],
  license_type: "Licence à vie",
  license_origin: "",
  duration: "À vie",
  devices: 1,
  compatibility: [],
  platform: "windows",
  delivery_mode: "license_key",
  delivery_instructions: "",
  delivery_time: "",
  service_label: "",
  required_fields: [],
  warranty: "Garantie d'activation 7 jours",
  downloads: [],
  is_active: true,
  is_featured: false,
  is_promo: false,
  promo_ends_at: "",
  is_subscription: false,
  stock_managed: true,
  manual_stock: 0,
  tags: [],
  is_demo: false,
  product_type: "",
};

const PRODUCT_TYPES = [
  ["", "Non — produit standard (sans téléchargement)"],
  ["software", "Logiciel"],
  ["game", "Jeu"],
  ["digital", "Produit numérique / Fichier"],
];

const PLATFORMS = ["windows", "mac", "multi", "android", "ios", "web"];
const DELIVERY = [
  ["license_key", "Clé de licence (stock)"],
  ["activation", "Activation manuelle (service serveur)"],
  ["instructions", "Instructions par email"],
  ["service", "Prestation de service"],
];

const FIELD_TYPES = [
  ["text", "🔤 Texte (Ligne unique)"],
  ["textarea", "📝 Texte long (Zone de texte)"],
  ["select", "📋 Liste déroulante"],
  ["radio", "🔘 Boutons radio"],
  ["checkbox", "☑️ Cases à cocher (Multi-choix)"],
  ["number", "🔢 Nombre"],
  ["email", "📧 E-mail"],
  ["tel", "📞 Téléphone / WhatsApp"],
];
const NEEDS_OPTIONS = ["select", "radio", "checkbox"];

const fieldKey = (label) =>
  label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [products, cats] = await Promise.all([
        api.get("/admin/products", { params: { q: query, limit: 100 } }),
        api.get("/admin/categories"),
      ]);
      setItems(products.data.items);
      setCategories(cats.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const bulk = useBulkDelete("/admin/products/bulk-delete", load, "produit(s)");

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, category_slug: categories[0]?.slug || "windows" });
    setOpen(true);
  };

  const openEdit = async (product) => {
    setEditing(product);
    setForm({
      ...EMPTY,
      ...product,
      promo_ends_at: product.promo_ends_at ? product.promo_ends_at.slice(0, 16) : "",
    });
    setOpen(true);
    if (!product.product_type) {
      try {
        const { data } = await api.get(`/admin/product-deliveries/by-product/${product.id}`);
        if (data && data.id) setForm((f) => ({ ...f, product_type: f.product_type || "digital" }));
      } catch {
        /* pas de configuration existante */
      }
    }
  };

  const set = (key) => (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      old_price: Number(form.old_price || 0),
      devices: Number(form.devices || 1),
      manual_stock: Number(form.manual_stock || 0),
      features: Array.isArray(form.features) ? form.features : String(form.features).split("\n").filter(Boolean),
      compatibility: Array.isArray(form.compatibility)
        ? form.compatibility
        : String(form.compatibility).split(",").map((s) => s.trim()).filter(Boolean),
      tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((s) => s.trim()).filter(Boolean),
      promo_ends_at: form.promo_ends_at ? new Date(form.promo_ends_at).toISOString() : "",
      is_promo: Number(form.old_price || 0) > Number(form.price),
      required_fields: (form.required_fields || [])
        .filter((f) => f.label?.trim())
        .map((f) => ({
          key: f.key || fieldKey(f.label),
          label: f.label.trim(),
          type: f.type || "text",
          placeholder: f.placeholder || "",
          required: f.required !== false,
          help: f.help || "",
          options: NEEDS_OPTIONS.includes(f.type) ? (f.options || []).map((o) => String(o).trim()).filter(Boolean) : [],
        })),
      custom_specs: (form.custom_specs || [])
        .filter((s) => s.label?.trim())
        .map((s) => ({ label: s.label.trim(), value: s.value || "" })),
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Produit mis à jour" : "Produit créé");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (product) => {
    if (!window.confirm(`Supprimer « ${product.name} » ?`)) return;
    try {
      await api.delete(`/admin/products/${product.id}`);
      toast.success("Produit supprimé");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const featuresText = useMemo(
    () => (Array.isArray(form.features) ? form.features.join("\n") : form.features || ""),
    [form.features],
  );

  const appendLink = (key) => (token) =>
    setForm((f) => ({ ...f, [key]: `${f[key] ? `${f[key]} ` : ""}${token}` }));

  const appendButton = (key) => (token) =>
    setForm((f) => ({ ...f, [key]: `${f[key] ? `${f[key]}\n` : ""}${token}` }));

  return (
    <AdminCard
      title="Produits"
      description={`${items.length} produit(s) dans le catalogue`}
      action={
        <div className="flex items-center gap-2">
          <span className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="admin-product-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher..."
              className="h-10 w-44 rounded-lg border border-line pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
            />
          </span>
          <button
            data-testid="admin-add-product"
            onClick={openCreate}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand-dark"
          >
            <Plus className="h-3.5 w-3.5" /> Nouveau produit
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-14">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-products" />
        <TableWrap>
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="border-b border-line text-left">
                <Th className="w-9">
                  <SelectCheckbox testId="bulk-all-products"
                    checked={items.length > 0 && bulk.selected.length >= items.length}
                    onChange={() => bulk.toggleAll(items.map((p) => p.id))} />
                </Th>
                <Th>Produit</Th>
                <Th>Catégorie</Th>
                <Th>Prix</Th>
                <Th>Stock</Th>
                <Th>Statut</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="admin-products-table">
              {items.map((product) => (
                <tr key={product.id} className="border-b border-line last:border-0" data-testid={`admin-product-row-${product.slug}`}>
                  <Td>
                    <SelectCheckbox testId={`bulk-product-${product.slug}`}
                      checked={bulk.selected.includes(product.id)} onChange={() => bulk.toggle(product.id)} />
                  </Td>
                  <Td>
                    <span className="flex items-center gap-3">
                      <img
                        src={fileUrl(product.image_url)}
                        alt=""
                        className="h-10 w-10 rounded-lg border border-line bg-slate-50 object-contain p-1"
                      />
                      <span className="min-w-0">
                        <span className="block max-w-[260px] truncate font-semibold">{product.name}</span>
                        <span className="block text-[11px] text-slate-400">/{product.slug}</span>
                      </span>
                    </span>
                  </Td>
                  <Td className="text-slate-500">{product.category_slug}</Td>
                  <Td>
                    <span className="font-bold">{formatPrice(product.price, "XOF")}</span>
                    {product.old_price > product.price && (
                      <span className="ml-1.5 text-[11px] text-slate-400 line-through">
                        {formatPrice(product.old_price, "XOF")}
                      </span>
                    )}
                  </Td>
                  <Td>
                    {product.delivery_mode === "license_key" && product.stock_managed ? (
                      <Pill tone={product.stock > 5 ? "green" : product.stock > 0 ? "amber" : "rose"}>
                        {product.stock} clé(s)
                      </Pill>
                    ) : (
                      <Pill tone="slate">Sur demande</Pill>
                    )}
                  </Td>
                  <Td>
                    <span className="flex flex-wrap gap-1">
                      <Pill tone={product.is_active ? "green" : "slate"}>
                        {product.is_active ? "Actif" : "Inactif"}
                      </Pill>
                      {product.is_featured && <Pill tone="blue">Vedette</Pill>}
                    </span>
                  </Td>
                  <Td className="text-right">
                    <span className="flex justify-end gap-1.5">
                      <button
                        data-testid={`admin-edit-product-${product.slug}`}
                        onClick={() => openEdit(product)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`admin-delete-product-${product.slug}`}
                        onClick={() => remove(product)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-rose-300 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        </>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title={editing ? "Modifier le produit" : "Nouveau produit"} wide>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-product-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom du produit">
              <input data-testid="product-form-name" required value={form.name} onChange={set("name")} className={inputCls} />
            </Field>
            <Field label="URL (slug)" hint="Laisser vide pour générer automatiquement">
              <input data-testid="product-form-slug" value={form.slug || ""} onChange={set("slug")} className={inputCls} />
            </Field>
          </div>

          <ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} label="Image du produit" testId="product-image" />

          <Field label="Description courte" hint="Vous pouvez insérer des liens cliquables (couleur, taille et police personnalisables)">
            <input data-testid="product-form-short" value={form.short_description} onChange={set("short_description")} className={inputCls} />
            <div className="flex flex-wrap gap-4">
              <LinkInserter testId="short" onInsert={appendLink("short_description")} />
              <ButtonInserter testId="short" onInsert={appendButton("short_description")} />
            </div>
          </Field>

          <Field label="Description complète" hint="Éditeur visuel : mise en forme, alignement, couleurs, tailles, listes, titres, liens, boutons, images et cadres personnalisés. L'aperçu correspond exactement à la fiche produit.">
            <RichTextEditor
              testId="description"
              value={form.description}
              onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            />
          </Field>

          <Field label="Caractéristiques (une par ligne)">
            <textarea
              data-testid="product-form-features"
              rows={4}
              value={featuresText}
              onChange={(e) => setForm((f) => ({ ...f, features: e.target.value.split("\n") }))}
              className={textareaCls}
            />
          </Field>

          <Field label="Informations détaillées (libellé / valeur)" hint="Ces paires s'affichent dans le tableau d'informations de la fiche produit, à côté des infos automatiques">
            <div className="space-y-2" data-testid="product-form-specs">
              {(form.custom_specs || []).map((spec, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    data-testid={`product-spec-label-${index}`}
                    value={spec.label}
                    onChange={(e) =>
                      setForm((f) => {
                        const next = [...f.custom_specs];
                        next[index] = { ...next[index], label: e.target.value };
                        return { ...f, custom_specs: next };
                      })
                    }
                    placeholder="Libellé (ex : Format)"
                    className={inputCls}
                  />
                  <input
                    data-testid={`product-spec-value-${index}`}
                    value={spec.value}
                    onChange={(e) =>
                      setForm((f) => {
                        const next = [...f.custom_specs];
                        next[index] = { ...next[index], value: e.target.value };
                        return { ...f, custom_specs: next };
                      })
                    }
                    placeholder="Valeur (ex : ISO 64 bits)"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    data-testid={`product-spec-remove-${index}`}
                    onClick={() => setForm((f) => ({ ...f, custom_specs: f.custom_specs.filter((_, i) => i !== index) }))}
                    title="Retirer"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line text-slate-400 transition-colors duration-150 hover:border-rose-300 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="product-spec-add"
                onClick={() => setForm((f) => ({ ...f, custom_specs: [...(f.custom_specs || []), { label: "", value: "" }] }))}
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-xs font-bold text-slate-500 transition-colors duration-150 hover:border-brand hover:text-brand"
              >
                <Plus className="h-3.5 w-3.5" /> Ajouter une information
              </button>
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Catégorie">
              <select data-testid="product-form-category" value={form.category_slug} onChange={set("category_slug")} className={inputCls}>
                {categories.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Prix (FCFA)">
              <input data-testid="product-form-price" type="number" required min={0} value={form.price} onChange={set("price")} className={inputCls} />
            </Field>
            <Field label="Ancien prix (FCFA)" hint="0 = pas de promotion">
              <input data-testid="product-form-old-price" type="number" min={0} value={form.old_price} onChange={set("old_price")} className={inputCls} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Type de licence">
              <input data-testid="product-form-license-type" value={form.license_type} onChange={set("license_type")} className={inputCls} />
            </Field>
            <Field label="Durée">
              <input data-testid="product-form-duration" value={form.duration} onChange={set("duration")} className={inputCls} />
            </Field>
            <Field label="Nombre d'appareils">
              <input data-testid="product-form-devices" type="number" min={1} value={form.devices} onChange={set("devices")} className={inputCls} />
            </Field>
          </div>

          <Field label="Provenance / type de licence (obligatoire avant mise en vente réelle)">
            <textarea data-testid="product-form-origin" rows={2} value={form.license_origin} onChange={set("license_origin")} className={textareaCls} />
            <LinkInserter testId="origin" onInsert={appendLink("license_origin")} />
            <ButtonInserter testId="origin" onInsert={appendButton("license_origin")} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Compatibilité (séparée par des virgules)">
              <input
                data-testid="product-form-compatibility"
                value={Array.isArray(form.compatibility) ? form.compatibility.join(", ") : form.compatibility}
                onChange={(e) => setForm((f) => ({ ...f, compatibility: e.target.value.split(",") }))}
                className={inputCls}
              />
            </Field>
            <Field label="Mots-clés (tags)">
              <input
                data-testid="product-form-tags"
                value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
                onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value.split(",") }))}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Plateforme">
              <select data-testid="product-form-platform" value={form.platform} onChange={set("platform")} className={inputCls}>
                {PLATFORMS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Field>
            <Field label="Mode de livraison">
              <select data-testid="product-form-delivery" value={form.delivery_mode} onChange={set("delivery_mode")} className={inputCls}>
                {DELIVERY.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label="Fin de promotion">
              <input data-testid="product-form-promo-end" type="datetime-local" value={form.promo_ends_at} onChange={set("promo_ends_at")} className={inputCls} />
            </Field>
          </div>

          <Field label="Instructions de livraison">
            <textarea data-testid="product-form-instructions" rows={2} value={form.delivery_instructions} onChange={set("delivery_instructions")} className={textareaCls} />
            <LinkInserter testId="instructions" onInsert={appendLink("delivery_instructions")} />
            <ButtonInserter testId="instructions" onInsert={appendButton("delivery_instructions")} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Badge de service" hint="Ex : Service Serveur — affiché sur la fiche produit">
              <input data-testid="product-form-service-label" value={form.service_label} onChange={set("service_label")} className={inputCls} placeholder="Service Serveur" />
            </Field>
            <Field label="Délai de livraison" hint="Ex : 1 à 60 minutes">
              <input data-testid="product-form-delivery-time" value={form.delivery_time} onChange={set("delivery_time")} className={inputCls} placeholder="1 à 60 minutes" />
            </Field>
          </div>

          <div className="rounded-xl border border-line bg-surface p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-ink">Champs à demander au client</p>
                <p className="text-[11px] text-slate-500">
                  Le client devra remplir ces champs sur la fiche produit avant de commander (ex : Nom d'utilisateur, IMEI...).
                </p>
              </div>
              <button
                type="button"
                data-testid="product-add-field"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    required_fields: [...(f.required_fields || []), { label: "", type: "text", required: true, placeholder: "", key: "", options: [] }],
                  }))
                }
                className="flex items-center gap-1.5 rounded-lg border border-brand px-3 py-2 text-xs font-bold text-brand hover:bg-brand-soft"
              >
                <Plus className="h-3.5 w-3.5" /> Ajouter un champ
              </button>
            </div>
            <div className="space-y-2" data-testid="product-fields-list">
              {(form.required_fields || []).map((field, index) => (
                <div key={index} className="grid gap-2 rounded-lg border border-line bg-white p-3 sm:grid-cols-[1fr_1fr_130px_auto_auto]">
                  <input
                    data-testid={`product-field-label-${index}`}
                    placeholder="Libellé (ex : IMEI)"
                    value={field.label || ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, label: e.target.value } : x)),
                      }))
                    }
                    className="h-10 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
                  />
                  <input
                    placeholder="Placeholder (optionnel)"
                    value={field.placeholder || ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, placeholder: e.target.value } : x)),
                      }))
                    }
                    className="h-10 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
                  />
                  <select
                    data-testid={`product-field-type-${index}`}
                    value={field.type || "text"}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, type: e.target.value } : x)),
                      }))
                    }
                    className="h-10 rounded-lg border border-line px-2 text-xs font-semibold"
                  >
                    {FIELD_TYPES.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-ink">
                    <input
                      type="checkbox"
                      data-testid={`product-field-required-${index}`}
                      checked={field.required !== false}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, required: e.target.checked } : x)),
                        }))
                      }
                      className="h-4 w-4 rounded border-slate-300 text-brand"
                    />
                    Obligatoire
                  </label>
                  <button
                    type="button"
                    data-testid={`product-field-remove-${index}`}
                    onClick={() =>
                      setForm((f) => ({ ...f, required_fields: f.required_fields.filter((_, i) => i !== index) }))
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-slate-400 hover:border-rose-300 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  {NEEDS_OPTIONS.includes(field.type) && (
                    <input
                      data-testid={`product-field-options-${index}`}
                      placeholder="Options séparées par des virgules (ex : Rouge, Vert, Bleu)"
                      value={(field.options || []).join(", ")}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          required_fields: f.required_fields.map((x, i) => (i === index ? { ...x, options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) } : x)),
                        }))
                      }
                      className="h-10 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none sm:col-span-full"
                    />
                  )}
                </div>
              ))}
              {!(form.required_fields || []).length && (
                <p className="rounded-lg border border-dashed border-line py-4 text-center text-xs text-slate-400">
                  Aucun champ — le client commande sans informations supplémentaires.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Garantie">
              <input data-testid="product-form-warranty" value={form.warranty} onChange={set("warranty")} className={inputCls} />
            </Field>
            <Field label="Stock manuel" hint="Utilisé si la gestion par clés est désactivée">
              <input data-testid="product-form-manual-stock" type="number" min={0} value={form.manual_stock} onChange={set("manual_stock")} className={inputCls} />
            </Field>
          </div>

          <Field label="Conditions d'utilisation">
            <textarea data-testid="product-form-conditions" rows={3} value={form.conditions} onChange={set("conditions")} className={textareaCls} />
            <LinkInserter testId="conditions" onInsert={appendLink("conditions")} />
            <ButtonInserter testId="conditions" onInsert={appendButton("conditions")} />
          </Field>

          <div className="space-y-3 rounded-xl border border-line bg-surface p-4" data-testid="product-downloads-section">
            <Field label="Ce produit nécessite un téléchargement ?" hint="Choisissez un type pour préparer à l'avance les fichiers, liens, clés et instructions livrés automatiquement au client après validation du paiement.">
              <select data-testid="product-form-type" value={form.product_type || ""} onChange={set("product_type")} className={inputCls}>
                {PRODUCT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </Field>
            {form.product_type ? <DownloadsManager productId={editing?.id} scope="admin" /> : null}
          </div>

          <div className="flex flex-wrap gap-4 rounded-xl border border-line bg-surface p-4">
            {[
              ["is_active", "Produit actif"],
              ["is_featured", "Produit vedette"],
              ["is_subscription", "Abonnement"],
              ["stock_managed", "Gérer le stock par clés"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm font-semibold text-ink">
                <input
                  type="checkbox"
                  data-testid={`product-form-${key}`}
                  checked={!!form[key]}
                  onChange={set(key)}
                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                />
                {label}
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex items-center gap-1.5 rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-ink"
            >
              <X className="h-4 w-4" /> Annuler
            </button>
            <button
              type="submit"
              data-testid="product-form-submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {editing ? "Enregistrer" : "Créer le produit"}
            </button>
          </div>
        </form>
      </AdminModal>
    </AdminCard>
  );
}
